import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { FALLBACK_ROUTE, isUserRole, USER_ROLES, UserRole } from '../auth/route-access';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.authorize(route, state);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.authorize(childRoute, state);
  }

  private authorize(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    const isLoggedIn = this.authService.isLoggedIn();
    if (!isLoggedIn) {
      if (this.authService.hasStoredToken()) {
        // Initial navigation belongs to the returned UrlTree, so only clear the
        // expired persisted session here and avoid a competing navigation.
        this.authService.expireSession(false);
      }
      return this.router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
    }

    const userRole = this.authService.getUserRole();
    if (!isUserRole(userRole)) {
      this.authService.expireSession(false);
      return this.router.createUrlTree(['/login']);
    }

    const allowedRoles: readonly UserRole[] = route.data['roles'] ?? USER_ROLES;
    if (allowedRoles.includes(userRole)) {
      return true;
    }

    return this.router.createUrlTree([FALLBACK_ROUTE]);
  }
}
